/**
 * Custom TipTap node for inline entity links (e.g., [[Task Name]], [[Meeting]]).
 *
 * Rendered as a clickable inline chip with an entity-type icon.
 * The suggestion is configured via EntityLinkExtension.ts and attached
 * to this node via addProseMirrorPlugins.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import type { SuggestionOptions } from '@tiptap/suggestion';
import type { SuggestionItem } from './SuggestionList';

export interface EntityLinkOptions {
  suggestion: Omit<SuggestionOptions<SuggestionItem>, 'editor'>;
  HTMLAttributes: Record<string, string>;
}

export const EntityLinkNode = Node.create<EntityLinkOptions>({
  name: 'entityLink',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      suggestion: {} as Omit<SuggestionOptions<SuggestionItem>, 'editor'>,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      entityType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) => ({ 'data-entity-type': attributes.entityType }),
      },
      entityId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-id'),
        renderHTML: (attributes) => ({ 'data-entity-id': attributes.entityId }),
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => ({ 'data-label': attributes.label }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="entity-link"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'entity-link',
        class: `entity-link-node entity-link-${node.attrs.entityType}`,
      }),
      node.attrs.label,
    ];
  },

  renderText({ node }) {
    return `[[${node.attrs.label}]]`;
  },

  addKeyboardShortcuts() {
    return {};
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
